import { useState, useEffect } from "react"

import { secondsToTime, SECOND_IN_MS } from "@/utils/time"

const REFERRAL_COUNTDOWN_INTERVAL_IN_MS = 30000
const REFERRAL_COUNTER_ANIMATION_DURATION_IN_MS = 900
const REFERRAL_COUNTER_ANIMATION_INTERVAL_IN_MS = 40

const useAnimatedCounter = (targetValue: number): number => {
	const [currentValue, setCurrentValue] = useState<number>(0)

	useEffect(() => {
		setCurrentValue(0)
		const startTime = Date.now()

		const intervalId = setInterval(() => {
			const elapsed = Date.now() - startTime
			const progress = Math.min(elapsed / REFERRAL_COUNTER_ANIMATION_DURATION_IN_MS, 1)
			const easedProgress = 1 - Math.pow(1 - progress, 3)

			setCurrentValue(Math.round(targetValue * easedProgress))

			if (progress >= 1) {
				clearInterval(intervalId)
			}
		}, REFERRAL_COUNTER_ANIMATION_INTERVAL_IN_MS)

		return () => clearInterval(intervalId)
	}, [targetValue])

	return currentValue
}

const formatCountdown = (deadline: string): string => {
	const deadlineDate = new Date(deadline)
	const now = new Date()
	const diffInMs = deadlineDate.getTime() - now.getTime()

	if (diffInMs <= 0) {
		return "poucas horas"
	}

	const hoursInDay = 24
	const diffInSeconds = diffInMs / SECOND_IN_MS
	const timeBreakdown = secondsToTime(diffInSeconds)
	const totalHours = timeBreakdown.hours
	const diffInMinutes = timeBreakdown.minutes

	const diffInDays = Math.floor(totalHours / hoursInDay)
	const remainingHours = totalHours % hoursInDay

	if (diffInDays >= 1) {
		return `${diffInDays} dia${diffInDays > 1 ? "s" : ""} e ${remainingHours}h`
	}

	return `${remainingHours}h ${diffInMinutes}min`
}

const useCountdown = (deadline: string): string => {
	const [countdownText, setCountdownText] = useState<string>(() => formatCountdown(deadline))

	useEffect(() => {
		const intervalId = setInterval(() => {
			setCountdownText(formatCountdown(deadline))
		}, REFERRAL_COUNTDOWN_INTERVAL_IN_MS)

		return () => clearInterval(intervalId)
	}, [deadline])

	return countdownText
}

export {
	useAnimatedCounter,
	useCountdown
}
